/**
 * WYSIWYG editor for a generated strategy document.
 *
 * The document is stored as markdown (rendered read-only by
 * `PlaybookRenderer`). This component edits that same markdown visually:
 * TipTap provides the rich-text surface, `tiptap-markdown` round-trips
 * markdown in (initial content) and out (on every change), and `TableKit`
 * keeps the doc's tables editable. Source-of-truth stays markdown — the
 * parent saves `onChange`'s latest string back to GCS.
 *
 * The component is intentionally uncontrolled (TipTap owns its document); it
 * seeds from `initialMarkdown` once on mount and reports edits via `onChange`.
 */

import { useEditor, EditorContent, useEditorState, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TableKit } from '@tiptap/extension-table';
import { Markdown } from 'tiptap-markdown';
import {
  Bold,
  Italic,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Table as TableIcon,
  Undo2,
  Redo2,
} from 'lucide-react';

import { cn } from '@/lib/utils';

interface StrategyDocEditorProps {
  /** Initial markdown to seed the editor with (read once on mount). */
  initialMarkdown: string;
  /** Fires with the full serialised markdown on every edit. */
  onChange: (markdown: string) => void;
}

function ToolbarButton({
  onClick,
  active,
  disabled,
  label,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type='button'
      aria-label={label}
      title={label}
      onMouseDown={(e) => e.preventDefault()} // keep editor selection on click
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded text-gray-700 transition-colors',
        'hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40',
        active && 'bg-[#EDE9FE] text-[#290BD9] hover:bg-[#EDE9FE]',
      )}
    >
      {children}
    </button>
  );
}

const TOOLBAR_DIVIDER = <span className='mx-1 h-5 w-px bg-gray-200' />;

/**
 * Reconcile `tiptap-markdown`'s serialized output with the conventions the
 * strategy doc uses, so an open→save round-trip is faithful.
 *
 * The doc's cover block and §1 hero stats are multi-line blockquotes whose
 * every line starts with a bold label, e.g.
 *
 *     > **1** — LIVE CAMPAIGNS active
 *     > **9,133** — KEYWORDS present
 *     > **28,796** — NEGATIVE KEYWORDS applied
 *
 * The source separates those lines with plain single-newline *soft* breaks,
 * and `PlaybookRenderer` keys its 3-card hero grid off those literal newlines.
 * ProseMirror, however, models every in-paragraph break as a *hard* break, so
 * `tiptap-markdown` re-serializes each line with two defects:
 *
 *   1. a doubled strong opener for the bold run that starts a hard-break
 *      continuation line — `\` + newline + `****9,133**` (should be `**9,133**`);
 *   2. the break itself as a backslash hard break (`\` + newline) rather than
 *      the plain newline the source (and the hero detector) expect.
 *
 * Both are fixed deterministically below. Order matters: collapse the `****`
 * while the hard break still anchors it, then demote the hard break to a soft
 * one. With `breaks: true` on the parse side this is idempotent — a re-loaded
 * soft break becomes a hard break again, which we re-normalise on the next
 * save — so the persisted markdown stays clean and stable.
 */
function repairTiptapMarkdown(markdown: string): string {
  return markdown
    .replace(/(\\\n)\*\*\*\*/g, '$1**')
    .replace(/\\\n/g, '\n');
}

// `tiptap-markdown` augments editor storage with a `markdown` serializer, but
// its module augmentation isn't always picked up by the build's type graph —
// read it through a narrow cast rather than loosening the whole storage type.
function getMarkdown(editor: Editor): string {
  const raw = (editor.storage as unknown as {
    markdown: { getMarkdown: () => string };
  }).markdown.getMarkdown();
  return repairTiptapMarkdown(raw);
}

export function StrategyDocEditor({ initialMarkdown, onChange }: StrategyDocEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TableKit.configure({ table: { resizable: true } }),
      // html:false keeps round-tripping deterministic — the doc is pure
      // markdown (no embedded HTML), so we never want raw HTML smuggled in.
      // breaks:true preserves the source's single-newline blockquote/prose
      // line breaks across an edit (see repairTiptapMarkdown); without it the
      // multi-line cover/hero blocks collapse onto one line on first save.
      Markdown.configure({ html: false, transformPastedText: true, breaks: true }),
    ],
    content: initialMarkdown,
    editorProps: {
      attributes: { class: 'sd-prose focus:outline-none' },
    },
    onUpdate: ({ editor }) => {
      onChange(getMarkdown(editor));
    },
  });

  // useEditorState gives reactive active/disabled flags for the toolbar without
  // re-rendering on every keystroke of unrelated state.
  const state = useEditorState({
    editor,
    selector: ({ editor }) => {
      if (!editor) return null;
      return {
        bold: editor.isActive('bold'),
        italic: editor.isActive('italic'),
        strike: editor.isActive('strike'),
        code: editor.isActive('code'),
        h1: editor.isActive('heading', { level: 1 }),
        h2: editor.isActive('heading', { level: 2 }),
        h3: editor.isActive('heading', { level: 3 }),
        bullet: editor.isActive('bulletList'),
        ordered: editor.isActive('orderedList'),
        quote: editor.isActive('blockquote'),
        inTable: editor.isActive('table'),
        canUndo: editor.can().undo(),
        canRedo: editor.can().redo(),
      };
    },
  });

  if (!editor || !state) {
    return (
      <div className='px-6 py-6 text-sm text-gray-500'>Preparing editor...</div>
    );
  }

  return (
    <div className='sd-editor'>
      <style>{`
        .sd-editor .sd-prose { color: #1f2937; line-height: 1.65; font-size: 0.95rem; }
        .sd-editor .sd-prose:focus { outline: none; }
        .sd-editor .sd-prose > * + * { margin-top: 0.75rem; }
        .sd-editor .sd-prose h1 { font-size: 1.75rem; font-weight: 700; color: #0f172a; margin-top: 1.5rem; }
        .sd-editor .sd-prose h2 { font-size: 1.4rem; font-weight: 700; color: #0f172a; margin-top: 1.5rem; padding-bottom: 0.25rem; border-bottom: 1px solid #e5e7eb; }
        .sd-editor .sd-prose h3 { font-size: 1.1rem; font-weight: 600; color: #0f172a; margin-top: 1rem; }
        .sd-editor .sd-prose ul { list-style: disc; margin-left: 1.5rem; }
        .sd-editor .sd-prose ol { list-style: decimal; margin-left: 1.5rem; }
        .sd-editor .sd-prose li > p { margin: 0; }
        .sd-editor .sd-prose blockquote { border-left: 4px solid #290BD9; background: #f9fafb; padding: 0.5rem 1rem; color: #374151; }
        .sd-editor .sd-prose a { color: #290BD9; text-decoration: underline; }
        .sd-editor .sd-prose code { background: #f3f4f6; border-radius: 0.25rem; padding: 0.1rem 0.35rem; font-size: 0.8rem; font-family: ui-monospace, monospace; }
        .sd-editor .sd-prose strong { font-weight: 700; color: #0f172a; }
        .sd-editor .sd-prose table { border-collapse: collapse; width: 100%; margin: 0.5rem 0; font-size: 0.85rem; }
        .sd-editor .sd-prose th, .sd-editor .sd-prose td { border: 1px solid #d1d5db; padding: 0.4rem 0.6rem; text-align: left; vertical-align: top; }
        .sd-editor .sd-prose th { background: #f9fafb; font-weight: 600; color: #0f172a; }
        .sd-editor .sd-prose .selectedCell { background: #EDE9FE; }
        .sd-editor .sd-prose .column-resize-handle { background: #290BD9; width: 2px; }
        .sd-editor .sd-prose p.is-editor-empty:first-child::before { content: attr(data-placeholder); color: #9ca3af; float: left; height: 0; pointer-events: none; }
      `}</style>

      {/* Sticky toolbar sits just under the dialog's own sticky action bar. */}
      <div className='sticky top-[49px] z-10 flex flex-wrap items-center gap-0.5 border-b bg-white/95 px-4 py-1.5 backdrop-blur'>
        <ToolbarButton label='Undo' onClick={() => editor.chain().focus().undo().run()} disabled={!state.canUndo}>
          <Undo2 className='h-4 w-4' />
        </ToolbarButton>
        <ToolbarButton label='Redo' onClick={() => editor.chain().focus().redo().run()} disabled={!state.canRedo}>
          <Redo2 className='h-4 w-4' />
        </ToolbarButton>
        {TOOLBAR_DIVIDER}
        <ToolbarButton label='Heading 1' active={state.h1} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
          <Heading1 className='h-4 w-4' />
        </ToolbarButton>
        <ToolbarButton label='Heading 2' active={state.h2} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 className='h-4 w-4' />
        </ToolbarButton>
        <ToolbarButton label='Heading 3' active={state.h3} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <Heading3 className='h-4 w-4' />
        </ToolbarButton>
        {TOOLBAR_DIVIDER}
        <ToolbarButton label='Bold' active={state.bold} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className='h-4 w-4' />
        </ToolbarButton>
        <ToolbarButton label='Italic' active={state.italic} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className='h-4 w-4' />
        </ToolbarButton>
        <ToolbarButton label='Strikethrough' active={state.strike} onClick={() => editor.chain().focus().toggleStrike().run()}>
          <Strikethrough className='h-4 w-4' />
        </ToolbarButton>
        <ToolbarButton label='Inline code' active={state.code} onClick={() => editor.chain().focus().toggleCode().run()}>
          <Code className='h-4 w-4' />
        </ToolbarButton>
        {TOOLBAR_DIVIDER}
        <ToolbarButton label='Bullet list' active={state.bullet} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className='h-4 w-4' />
        </ToolbarButton>
        <ToolbarButton label='Numbered list' active={state.ordered} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className='h-4 w-4' />
        </ToolbarButton>
        <ToolbarButton label='Quote' active={state.quote} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quote className='h-4 w-4' />
        </ToolbarButton>
        {TOOLBAR_DIVIDER}
        <ToolbarButton
          label={state.inTable ? 'Delete table' : 'Insert table'}
          active={state.inTable}
          onClick={() =>
            state.inTable
              ? editor.chain().focus().deleteTable().run()
              : editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          }
        >
          <TableIcon className='h-4 w-4' />
        </ToolbarButton>
      </div>

      <EditorContent editor={editor} className='px-6 py-6' />
    </div>
  );
}
